import { FC, useEffect, useState } from 'react'
import { ReactComponent as AiSparkleIcon } from 'src/assets/svg/ai-sparkle.svg'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import {
  MentionSentiment,
  TopicDetail,
  TopicTableEntry,
  TopicsTableData,
  barFillRatio,
} from 'src/interfaces/topics.interface'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { useGetTopicDetailQuery } from 'src/reactQuery/useGetSocialTopicsQuery'
import styles from './topicsTableSection.module.scss'
import { TopicsEmptyState } from '../TopicsEmptyState'
import { DeltaBadge } from '../DeltaBadge'
import { SentimentBar } from '../SentimentBar'
import { SentimentHoverCard } from '../SentimentHoverCard'
import { Sparkline } from '../Sparkline'
import { TopicDetailModal } from '../TopicDetailModal'
import { SOCIAL_TOPICS_COPY, TopicsCopy, countLabel } from '../copy'

interface TopicsTableSectionProps {
  // The campaign the topics belong to; the modal needs it to fetch a detail.
  campaignId?: string
  // Wording for the product the widget sits in; Social Listening by default.
  copy?: TopicsCopy
  data?: TopicsTableData
  // A host with its own detail read (Review Analysis) owns the query: it gets
  // the open topic/subtopic through `onSelectionChange` and hands the result
  // back here. Without it the section reads Social Listening's detail itself.
  detailData?: TopicDetail
  detailLoading?: boolean
  isLoading?: boolean
  onSelectionChange?: (
    topic: string | null,
    subtopic: string | null,
    sentiment: MentionSentiment | null
  ) => void
  range?: { fromDate?: string; toDate?: string }
}

const COPY = {
  emerging: 'Emerging Topics',
  emergingEmpty: 'Nothing new has surfaced in this period.',
  emergingSubtitle:
    "Automatically discovered — conversations gaining traction that aren't part of your predefined topics yet",
  formatPlatform: (platform: string) =>
    platform.charAt(0).toUpperCase() + platform.slice(1),
  new: 'NEW',
  painPoint: 'Pain point',
  praise: 'Praise',
  title: 'Topics & Subtopics',
}

// Replaced per product: "Total Mentions" or "Total Reviews".
const TOTAL_COLUMN = '__total__'

const COLUMNS = [
  'Rank',
  'Topic',
  TOTAL_COLUMN,
  'Share',
  'Trend',
  'Delta',
  'Top Source',
]

/**
 * The sparkline's colour comes from the series itself (last third vs first
 * third), falling back to the delta only when the series is too short to say.
 */
const isRising = (points: number[], delta: number | null): boolean => {
  if (points.length >= 4) {
    const third = Math.max(1, Math.floor(points.length / 3))
    const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0)
    return sum(points.slice(-third)) >= sum(points.slice(0, third))
  }
  return (delta ?? 0) >= 0
}

const TopicTableRow: FC<{
  entry: TopicTableEntry
  max: number
  onOpen: () => void
  rank: number
}> = ({ entry, max, onOpen, rank }) => (
  <div
    className={styles.row}
    onClick={onOpen}
    onKeyDown={(event) => {
      if (event.target === event.currentTarget && event.key === 'Enter') {
        onOpen()
      }
    }}
    role="button"
    tabIndex={0}
  >
    <span className={styles.rank}>{rank}</span>

    <div className={styles.topicCell}>
      <div className={styles.topicNameRow}>
        {/* Two lines, then ellipsis — the design's fifth row wraps onto a
            second line rather than truncating at the first. */}
        <TruncatedTooltip
          className={styles.topicName}
          maxLines={2}
          text={entry.name}
        />
        {entry.isNew && <span className={styles.newPill}>{COPY.new}</span>}
        {entry.polarity && (
          <span
            className={`${styles.polarityPill} ${
              entry.polarity === 'NEG' ? styles.polarityPillNeg : ''
            }`}
          >
            {entry.polarity === 'NEG' ? COPY.painPoint : COPY.praise}
          </span>
        )}
      </div>
      <SentimentHoverCard
        className={styles.topicBarWrap}
        sentiment={entry.sentiment}
        total={entry.mentions}
      >
        <SentimentBar
          className={styles.topicBar}
          fill={barFillRatio(entry.mentions, max)}
          sentiment={entry.sentiment}
        />
      </SentimentHoverCard>
    </div>

    <span className={styles.mentions}>{entry.mentions.toLocaleString()}</span>
    <span className={styles.share}>{entry.share}%</span>
    <span className={styles.trend}>
      <Sparkline
        points={entry.trend}
        rising={isRising(entry.trend, entry.delta)}
      />
    </span>
    <span className={styles.deltaCell}>
      {/* No baseline (lifetime view, brand-new topic) means no pill at all,
          not an empty capsule — the modal makes the same call. */}
      {entry.delta !== null && entry.delta !== undefined ? (
        <span
          className={`${styles.deltaPill} ${
            entry.delta >= 0 ? styles.deltaUp : styles.deltaDown
          }`}
        >
          <DeltaBadge value={entry.delta} />
        </span>
      ) : (
        <span className={styles.deltaNone}>—</span>
      )}
    </span>
    <span className={styles.sourceCell}>
      <span className={styles.sourcePill}>
        <span className={styles.sourceIcon}>
          {SOCIAL_ICONS[entry.topSource] ?? SOCIAL_ICONS.earth}
        </span>
        <span className={styles.sourceName}>
          {platformDisplayLabel(
            entry.topSource,
            COPY.formatPlatform(entry.topSource)
          )}
        </span>
      </span>
    </span>
    <span className={styles.chevron}>
      <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
        <path
          d="M6 3.5L10.5 8L6 12.5"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.6"
        />
      </svg>
    </span>
  </div>
)

export const TopicsTableSection: FC<TopicsTableSectionProps> = ({
  campaignId,
  copy = SOCIAL_TOPICS_COPY,
  data,
  detailData,
  detailLoading = false,
  isLoading = false,
  onSelectionChange,
  range,
}) => {
  const topics = data?.topics ?? []
  const emerging = data?.emerging ?? []
  const max = topics.reduce((acc, entry) => Math.max(acc, entry.mentions), 0)
  const [openTopic, setOpenTopic] = useState<string | null>(null)
  // The subtopic drilled into from the topic modal; null shows the topic.
  const [openSubtopic, setOpenSubtopic] = useState<string | null>(null)
  // The Related Mentions tab; null is "All". Resets whenever the view moves
  // between a topic and a subtopic.
  const [sentimentTab, setSentimentTab] = useState<MentionSentiment | null>(
    null
  )
  const hostOwnsDetail = !!onSelectionChange
  // Disabled (no campaign id) when the host supplies the detail itself.
  const social = useGetTopicDetailQuery(
    hostOwnsDetail ? undefined : campaignId,
    openTopic,
    range,
    openSubtopic,
    sentimentTab
  )
  const topicDetail = hostOwnsDetail ? detailData : social.data
  const topicDetailLoading = hostOwnsDetail ? detailLoading : social.isLoading
  useEffect(() => {
    onSelectionChange?.(openTopic, openSubtopic, sentimentTab)
  }, [onSelectionChange, openTopic, openSubtopic, sentimentTab])
  const openSubtopicView = (name: string | null) => {
    setOpenSubtopic(name)
    setSentimentTab(null)
  }
  const closeModal = () => {
    setOpenTopic(null)
    setOpenSubtopic(null)
    setSentimentTab(null)
  }

  // Nothing tagged: the whole section is the empty state, with no column
  // header or discovery panel around it, matching Smart Insights above.
  if (!isLoading && topics.length === 0) {
    return (
      <section className={styles.section}>
        <div className={styles.header}>
          <h2 className={styles.title}>{COPY.title}</h2>
          <p className={styles.subtitle}>{copy.subtitle}</p>
        </div>
        <TopicsEmptyState title={COPY.title} />
      </section>
    )
  }

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <h2 className={styles.title}>{COPY.title}</h2>
        <p className={styles.subtitle}>{copy.subtitle}</p>
      </div>

      <div className={styles.tableCard}>
        <div className={styles.tableHead}>
          {COLUMNS.map((column) => (
            <span className={styles.headCell} key={column}>
              {column === TOTAL_COLUMN
                ? `Total ${copy.noun.charAt(0).toUpperCase()}${copy.noun.slice(1)}`
                : column}
            </span>
          ))}
          {/* Keeps the header grid in step with the row's trailing chevron. */}
          <span />
        </div>

        {isLoading ? (
          <div className={styles.tableBody}>
            {/* Five rows, the page size the design shows, so the card does not
                resize when ~900ms of request returns. */}
            {Array.from({ length: 5 }).map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className={styles.row} key={`sk-${i}`}>
                <span className={styles.skeletonLine} />
                <span className={styles.skeletonLine} />
                <span className={styles.skeletonLine} />
                <span className={styles.skeletonLine} />
                <span className={styles.skeletonLine} />
                <span className={styles.skeletonLine} />
                <span className={styles.skeletonLine} />
                <span />
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.tableBody}>
            {topics.map((entry, index) => (
              <TopicTableRow
                entry={entry}
                key={entry.id}
                max={max}
                onOpen={() => setOpenTopic(entry.id)}
                rank={index + 1}
              />
            ))}
          </div>
        )}
      </div>

      {/* An empty discovery list is a real answer, so the panel stays and says
          so rather than vanishing and leaving the page looking truncated. */}
      <div className={styles.emerging}>
        <div className={styles.emergingHeader}>
          <span className={styles.emergingTitle}>
            <AiSparkleIcon className={styles.emergingIcon} />
            {COPY.emerging}
          </span>
          <p className={styles.emergingSubtitle}>{COPY.emergingSubtitle}</p>
        </div>
        {emerging.length === 0 ? (
          <span className={styles.emergingEmpty}>{COPY.emergingEmpty}</span>
        ) : (
          <div className={styles.emergingList}>
            {emerging.map((topic) => (
              <div className={styles.emergingCard} key={topic.id}>
                <TruncatedTooltip
                  className={styles.emergingName}
                  text={topic.name}
                />
                <div className={styles.emergingMeta}>
                  <span className={styles.emergingStat}>
                    {`${countLabel(topic.mentions, copy)} · ${topic.share}% ${copy.shareOfTotal}`}
                  </span>
                  {/* A brand-new topic has no previous period, so no pill at
                      all rather than an empty capsule. */}
                  {topic.delta !== null && topic.delta !== undefined && (
                    <span
                      className={`${styles.emergingDelta} ${
                        topic.delta < 0 ? styles.emergingDeltaDown : ''
                      }`}
                    >
                      <DeltaBadge value={topic.delta} />
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* One shared fixture until the endpoint exists; the real version keys
          the request off the topic that was opened. */}
      {openTopic && (
        <TopicDetailModal
          copy={copy}
          data={topicDetail}
          isLoading={topicDetailLoading}
          onBack={() => openSubtopicView(null)}
          onClose={closeModal}
          onOpenSubtopic={openSubtopicView}
          onSentimentChange={setSentimentTab}
          sentiment={sentimentTab}
          subtopic={openSubtopic}
          topic={openTopic}
        />
      )}
    </section>
  )
}
